const API_BASE_URL = 'https://moncopieur.cloud-erp.ma';

// Types based on OpenAPI schema v2.0.0
export interface OrderListItem {
  id: number;
  name: string;
  date: string;
  payment_method: 'cash_on_delivery' | 'transfer' | 'naps';
  payment_method_label: string;
  delivery_method: 'free_delivery' | 'store';
  delivery_method_label: string;
  amount: number;
  ecommerce_state: 'pending_confirmation' | 'pending_payment' | 'order_preparation' | 'shipped' | 'delivered' | 'cancelled';
  ecommerce_state_label: string;
  state: string;
  state_label: string;
  state_color: 'yellow' | 'green' | 'red';
}

export interface OrderListResponse {
  total: number;
  page: number;
  page_size: number;
  items: OrderListItem[];
}

export interface CreateOrderLine {
  product_id: number;
  qty: number;
}

export interface CreateOrderRequest {
  lines: CreateOrderLine[];
  payment_method: 'cash_on_delivery' | 'transfer' | 'naps';
  delivery_method: 'free_delivery' | 'store';
  delivery_address_id?: number;
  invoice_address_id?: number;
  additional_message?: string;
}

export interface CreateOrderResponse {
  success: boolean;
  order_id: number;
  name: string;
  amount: number;
  state: string;
  state_label: string;
  state_color: 'yellow' | 'green' | 'red';
  payment_method: 'cash_on_delivery' | 'transfer' | 'naps';
  delivery_method: 'free_delivery' | 'store';
  ecommerce_state: 'pending_confirmation' | 'pending_payment' | 'order_preparation' | 'shipped' | 'delivered' | 'cancelled';
}

// Helper function to get auth headers
const getAuthHeaders = (token: string) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json',
});

// List orders for authenticated partner
export const getOrders = async (
  token: string,
  page: number = 1,
  pageSize: number = 20
): Promise<OrderListResponse> => {
  const url = new URL(`${API_BASE_URL}/api/orders`);
  url.searchParams.set('page', page.toString());
  url.searchParams.set('page_size', pageSize.toString());

  console.log('Fetching orders from:', url.toString());
  console.log('Using token:', token ? 'Token present' : 'No token');

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });

    console.log('Orders API response status:', response.status);
    console.log('Orders API response:', response);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Orders API error response:', errorText);
      throw new Error(`Failed to fetch orders: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    console.log('Orders data received:', data);
    return data;
  } catch (error) {
    console.error('Orders fetch error:', error);
    throw error;
  }
};

// Create a new order
export const createOrder = async (
  token: string,
  orderData: CreateOrderRequest
): Promise<CreateOrderResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/orders`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify(orderData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(errorData.error || `Failed to create order: ${response.statusText}`);
  }

  return response.json();
};

// Download order PDF
export const downloadOrderPdf = async (
  token: string,
  orderId: number
): Promise<Blob> => {
  const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/pdf`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(errorData.error || `Failed to download order PDF: ${response.statusText}`);
  }

  return response.blob();
};


// Helper function to trigger file download
export const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Payment method labels
export const PAYMENT_METHOD_LABELS = {
  cash_on_delivery: 'Paiement à la livraison',
  transfer: 'Virement',
  naps: 'NAPS',
} as const;

// Delivery method labels
export const DELIVERY_METHOD_LABELS = {
  free_delivery: 'Livraison gratuite',
  store: 'Magasin',
} as const;

// Order state colors for UI
export const ORDER_STATE_COLORS = {
  yellow: 'En attente de confirmation',
  green: 'Confirmé', 
  red: 'Annulé',
} as const;